import { useCallback, useEffect, useRef, useState } from "react";

/** ~5 seconds on page before an engagement prompt may appear. */
export const ENGAGEMENT_SUBSCRIBE_DWELL_MS = 5_000;

/** Minimum vertical scroll before counting as engaged browsing. */
export const ENGAGEMENT_SUBSCRIBE_SCROLL_PX = 280;

const POLL_MS = 4_000;

function readSessionDismissed(key) {
  if (!key || typeof window === "undefined") return false;
  try {
    return Boolean(window.sessionStorage.getItem(key));
  } catch {
    return false;
  }
}

function writeSessionDismissed(key) {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Opens a subscribe / signup prompt after the visitor has been on the page for a while.
 * By default also requires a small scroll; set `requireScroll: false` for landing-page timers.
 */
export function useEngagementSubscribePrompt({
  enabled = true,
  dwellMs = ENGAGEMENT_SUBSCRIBE_DWELL_MS,
  scrollThresholdPx = ENGAGEMENT_SUBSCRIBE_SCROLL_PX,
  requireScroll = true,
  sessionDismissKey = "",
  /** When false, closing/dismissing only applies until the page is refreshed. */
  persistDismiss = true,
} = {}) {
  const [shouldOpen, setShouldOpen] = useState(false);
  const consumedRef = useRef(false);
  const landedAtRef = useRef(Date.now());
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    consumedRef.current = false;
    setShouldOpen(false);
    hasScrolledRef.current = false;
    landedAtRef.current = Date.now();

    if (!enabled) return undefined;

    if (!requireScroll) {
      hasScrolledRef.current = true;
    }

    if (persistDismiss && readSessionDismissed(sessionDismissKey)) {
      consumedRef.current = true;
      return undefined;
    }

    const consider = () => {
      if (consumedRef.current) return;
      if (!requireScroll || window.scrollY >= scrollThresholdPx) {
        hasScrolledRef.current = true;
      }
      const elapsed = Date.now() - landedAtRef.current;
      if (elapsed >= dwellMs && hasScrolledRef.current) {
        consumedRef.current = true;
        setShouldOpen(true);
      }
    };

    consider();
    const interval = window.setInterval(consider, POLL_MS);
    window.addEventListener("scroll", consider, { passive: true });

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("scroll", consider);
    };
  }, [enabled, dwellMs, scrollThresholdPx, requireScroll, sessionDismissKey, persistDismiss]);

  const dismiss = useCallback(() => {
    setShouldOpen(false);
    consumedRef.current = true;
    if (persistDismiss) {
      writeSessionDismissed(sessionDismissKey);
    }
  }, [sessionDismissKey, persistDismiss]);

  return { shouldOpen, dismiss };
}
